import type { LensCard, LensColumnKind, LensDataset } from '~/types/leLens';

/**
 * F8's remaining three actions, as pure logic: relabel a column, override
 * its kind, and remove a dataset (blocked while a card reads it). No
 * network call for any of the three; the caller saves the result through
 * the same lens update mutation every other dataset edit in this lane uses.
 *
 * Not wired to a trigger yet: `FieldList.tsx`'s row menu (`onRelabelField`,
 * `onChangeFieldKind`, `onRemoveField` on `LensEditPanel`) already opens for
 * relabel and kind, but takes no text input or kind picker of its own past
 * the menu click, and there is no dataset-level menu anywhere in the panel
 * yet for "remove a dataset" to hang off. Both are UI gaps in lanes this one
 * does not own (C1's `FieldList.tsx`/`LensEditPanel.tsx`, or C3's
 * migration-UX pass); flagged in the lane report rather than built as a
 * guess at their shape.
 */

/** Sets or clears a column's reader-facing label. An empty or whitespace-only label clears it,
 *  since a blank label is not a label at all, it is falling back to the column name. */
export function relabelDatasetColumn(dataset: LensDataset, columnName: string, label: string): LensDataset {
  const trimmed = label.trim();
  return {
    ...dataset,
    columns: (dataset.columns ?? []).map((column) =>
      column.name === columnName ? { ...column, label: trimmed || undefined } : column,
    ),
  };
}

/** Overrides a column's inferred kind (00_PLAN.md: "The author can override kind per column in
 *  the panel"), for the case kind inference gets wrong: a numeric id that is not a measure, or a
 *  measure whose name happens to end `_id`/`_bk`/`_key` and was demoted to a dimension. */
export function overrideColumnKind(dataset: LensDataset, columnName: string, kind: LensColumnKind): LensDataset {
  return {
    ...dataset,
    columns: (dataset.columns ?? []).map((column) => (column.name === columnName ? { ...column, kind } : column)),
  };
}

/** Drops a calculated field and its matching declared column (`FieldList.tsx`'s row menu offers
 *  Remove for a calculated field only, never a returned column). */
export function removeCalculatedField(dataset: LensDataset, fieldName: string): LensDataset {
  return {
    ...dataset,
    columns: (dataset.columns ?? []).filter((column) => column.name !== fieldName),
    fields: (dataset.fields ?? []).filter((field) => field.name !== fieldName),
  };
}

export type RemoveDatasetOutcome =
  | { kind: 'allowed'; nextDatasets: Array<LensDataset> }
  | { kind: 'blocked'; blockingCards: Array<{ id: string; title: string }> };

/** F8: "Remove a dataset: blocked while cards read it, the cards are listed with a link to
 *  each." The link itself is a UI concern for whoever renders `blockingCards`; this only decides
 *  whether the removal is allowed and names what is blocking it. */
export function removeDataset(
  datasetId: string,
  datasets: ReadonlyArray<LensDataset>,
  cards: ReadonlyArray<LensCard>,
): RemoveDatasetOutcome {
  const blockingCards = cards
    .filter((card) => card.datasetId === datasetId)
    .map((card) => ({ id: card.id, title: card.title }));
  if (blockingCards.length) {
    return { blockingCards, kind: 'blocked' };
  }
  return { kind: 'allowed', nextDatasets: datasets.filter((dataset) => dataset.id !== datasetId) };
}
